"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CourseVariant = void 0;
const StringBuilder_1 = require("../../utils/StringBuilder");
const BranchType_1 = require("../command/lib/BranchType");
const CommandType_1 = require("../command/lib/CommandType");
const MetadataProperty_1 = require("../common/MetadataProperty");
const Player_1 = require("../common/Player");
const NoteType_1 = require("../note/lib/NoteType");
const Style_1 = require("./lib/Style");
/**
 * Represents a single course variant (single or double) in a TJA chart file.
 */
class CourseVariant {
    constructor() {
        /**
         * Sets INIT value for the scoring method.
         */
        this.scoreInit = 390;
        /**
         * Sets DIFF value for the scoring method.
         */
        this.scoreDiff = 100;
        this._singleCommands = [];
        this._player1Commands = [];
        this._player2Commands = [];
        /**
         * Comma separated array of integers for Balloon notes (7) and Kusudama notes
         * (9). Required when balloon notes appear in the course. Amount of values in
         * the array should correspond to the amount of balloons in the course. The
         * balloon values are used as they appear in the chart and the values have to
         * be repeated when branches are used.
         */
        this.balloonCounts = [];
        /**
         * Comma separated array of integers for Balloon notes (7) and Kusudama notes
         * (9) for each branch in a course with diverge notes. Follows the same set of
         * rules as the `balloonCounts` property.
         */
        this.branchBalloonCounts = {
            [BranchType_1.BranchType.Normal]: [],
            [BranchType_1.BranchType.Advanced]: [],
            [BranchType_1.BranchType.Master]: [],
        };
        /**
         * Play the song notation after next #START depending on if playing in
         * singleplayer or multiplayer.
         */
        this.style = Style_1.Style.Single;
        /**
         * Hide the diverge notes indication on the song selection screen and current
         * branch in the game until branching actually starts.
         */
        this.hiddenBranches = false;
        // DOJO PROPERTIES START
        /**
         * Information related to the 3 gauges in a dojo course.
         */
        this.gauges = [
            null,
            null,
            null,
        ];
        // DOJO PROPERTIES END
        /**
         * Whether the course has branches. This is not affected by the
         * `hiddenBranches` property.
         */
        this.hasBranches = false;
        /**
         * Whether the course has balloon or kusudama notes.
         */
        this.hasBalloonNotes = false;
        /**
         * Whether the course has any kind of drumroll notes. Balloon notes are not
         * included.
         */
        this.hasDrumroll = false;
        this._currentBalloonNoteIndex = 0;
        this._currentBranchType = null;
    }
    /**
     * Returns the command buffer. When the course variant has the Single style,
     * the command buffer for the single variant will be returned. Otherwise, the
     * command buffer for the specified player will be returned, or the first
     * player's command buffer if no player is specified.
     *
     * @param player - An optional player to get the command buffer for when the
     * course variant has the Double style. If not provided, P1 commands will be
     * returned.
     * @returns An array of commands and note sequences.
     */
    getCommands(player) {
        if (this.style === Style_1.Style.Single) {
            return this._singleCommands;
        }
        switch (player) {
            case Player_1.Player.Player1:
            case undefined:
                return this._player1Commands;
            case Player_1.Player.Player2:
                return this._player2Commands;
        }
    }
    /**
     * Converts the course variant into TJA format.
     * @returns - A string that will correspond to a single course variant in a
     * TJA file.
     */
    toString() {
        const builder = new StringBuilder_1.StringBuilder()
            .withSeparator('\n')
            .append(`${MetadataProperty_1.CourseMetadataProperty.Style}:${this.style}`)
            .append(`${MetadataProperty_1.CourseMetadataProperty.ScoreInit}:${this.scoreInit}`)
            .append(`${MetadataProperty_1.CourseMetadataProperty.ScoreDiff}:${this.scoreDiff}`);
        if (this.balloonCounts.length > 0) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.Balloon}:${this.balloonCounts.join(',')}`);
        }
        if (this.branchBalloonCounts[BranchType_1.BranchType.Normal].length > 0) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.BalloonNor}:${this.branchBalloonCounts[BranchType_1.BranchType.Normal].join(',')}`);
        }
        if (this.branchBalloonCounts[BranchType_1.BranchType.Advanced].length > 0) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.BalloonExp}:${this.branchBalloonCounts[BranchType_1.BranchType.Advanced].join(',')}`);
        }
        if (this.branchBalloonCounts[BranchType_1.BranchType.Master].length > 0) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.BalloonMas}:${this.branchBalloonCounts[BranchType_1.BranchType.Advanced].join(',')}`);
        }
        if (this.hiddenBranches) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.HiddenBranch}:1`);
        }
        if (this.gauges[0]) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.Exam1}:${this.gauges[0].toString()}`);
        }
        if (this.gauges[1]) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.Exam2}:${this.gauges[1].toString()}`);
        }
        if (this.gauges[2]) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.Exam3}:${this.gauges[2].toString()}`);
        }
        if (this.gaugeIncrementMethod) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.GaugeIncr}:${this.gaugeIncrementMethod}`);
        }
        if (this.gaugeTotal) {
            builder.append(`${MetadataProperty_1.CourseMetadataProperty.Total}:${this.gaugeTotal}`);
        }
        builder.append('\n');
        this._singleCommands.forEach((command) => {
            builder.append(command.toString());
        });
        this._player1Commands.forEach((command) => {
            builder.append(command.toString());
        });
        this._player2Commands.forEach((command) => {
            builder.append(command.toString());
        });
        return builder.toString();
    }
    /**
     * Add a command to the command buffer.
     * @param command - The command object to add.
     */
    addCommand(command, player) {
        if (this.style === Style_1.Style.Single) {
            this._singleCommands.push(command);
        }
        else {
            switch (player) {
                case Player_1.Player.Player1:
                    this._player1Commands.push(command);
                    break;
                case Player_1.Player.Player2:
                    this._player2Commands.push(command);
                    break;
            }
        }
        this.postProcessCommand(command);
    }
    /**
     * Checks whether all command buffers are empty. This is useful for filtering
     * out course variants that aren't actually part of the TJA file.
     * @returns True if all command buffers are empty, false otherwise.
     */
    isEmpty() {
        return (this._singleCommands.length === 0 &&
            this._player1Commands.length === 0 &&
            this._player2Commands.length === 0);
    }
    /**
     * Sets the gauge at a given index to the provided gauge data.
     * @param index - The index to set.
     * @param gauge - The gauge data.
     */
    setGauge(index, gauge) {
        this.gauges[index] = gauge;
    }
    postProcessCommand(command) {
        if (command.commandType === CommandType_1.CommandType.BranchStart) {
            this.hasBranches = true;
            return;
        }
        if (command.commandType === CommandType_1.CommandType.BranchMarker) {
            this._currentBranchType = command.branchType;
            this._currentBalloonNoteIndex = 0;
            return;
        }
        if (command.commandType === CommandType_1.CommandType.NoteSequence) {
            for (const note of command.notes) {
                this.postProcessNote(note);
            }
            return;
        }
    }
    postProcessNote(note) {
        if (note.noteType === NoteType_1.NoteType.Balloon ||
            note.noteType === NoteType_1.NoteType.Kusudama) {
            return this.postProcessBalloonNote(note);
        }
        if (note.noteType === NoteType_1.NoteType.Drumroll ||
            note.noteType === NoteType_1.NoteType.DRUMROLL) {
            this.hasDrumroll = true;
            return;
        }
    }
    postProcessBalloonNote(note) {
        this.hasBalloonNotes = true;
        let counts = this.balloonCounts;
        if (this._currentBranchType) {
            counts = this.branchBalloonCounts[this._currentBranchType];
        }
        note.count = this.getBalloonCount(counts, this._currentBalloonNoteIndex);
    }
    getBalloonCount(source, index) {
        if (index >= source.length) {
            return 0;
        }
        return source[index];
    }
}
exports.CourseVariant = CourseVariant;
